import { MutableRefObject, useCallback, useEffect, useState } from 'react'
import { TurnstileInstance } from '@marsidev/react-turnstile'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { isOk } from 'utils/url/status'
import { Experiments, optimizelyClient } from 'CustomOptimizelyProvider'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { isCaptchaOnRender } from 'utils/entrypoint/isCaptchaOnRender'

type CaptchaTriggerSource = 'render' | 'ctaClick'

type UseCaptchaParams = {
  captchaRef: MutableRefObject<TurnstileInstance | null>
}

export const useCaptcha = ({ captchaRef }: UseCaptchaParams) => {
  const {
    page: { isLastDisplayedPage },
    traversal: { traversalId },
    navigation: { currentPageInSession },
    captcha: {
      postCaptchaToken,
      getCaptchaStatus,
      captchaFlowStatus,
      setCaptchaFlowStatus
    }
  } = useStores()

  const [captchaToken, setCaptchaToken] = useState<string>('')
  const urlParams = useUrlParams()

  const checkIfCaptchaKillSwitchIsEnabled = (): boolean => {
    const userContext = optimizelyClient.getUserContext()
    const captchaKillSwitch = userContext?.decide(
      Experiments.CAT_3614_CAPTCHA_KILL_SWITCH
    )

    if (!!captchaKillSwitch?.enabled) {
      trackInAmplitude(AmplitudeEvent.CaptchaKillSwitchIsActive, {
        currentPage: currentPageInSession
      })

      return true
    }

    return false
  }

  const shouldAwaitCaptcha = (): boolean => {
    return isLastDisplayedPage(currentPageInSession)
  }

  const shouldHandleCaptcha = (source: CaptchaTriggerSource): boolean => {
    if (checkIfCaptchaKillSwitchIsEnabled()) {
      return false
    }

    if (source === 'render') {
      // please update handleFinal in loadEntryPoint.ts if this logic changes
      return isCaptchaOnRender(urlParams)
    }

    if (source === 'ctaClick') {
      return (
        currentPageInSession === 'contact' ||
        isLastDisplayedPage(currentPageInSession)
      )
    }

    return false
  }

  const handleCaptcha = async (captchaTriggerSource: CaptchaTriggerSource) => {
    trackInAmplitude(AmplitudeEvent.CaptchaInitiated, {
      currentPage: currentPageInSession,
      captchaTriggerSource
    })

    // Check if the captcha is already successfully solved
    if (captchaFlowStatus === 'success') {
      trackInAmplitude(AmplitudeEvent.CaptchaSkipped, {
        currentPage: currentPageInSession
      })

      return
    }

    // Check if the captcha flow is already in progress
    if (captchaFlowStatus === 'inProgress') {
      trackInAmplitude(AmplitudeEvent.CaptchaAlreadyInProgress, {
        currentPage: currentPageInSession
      })

      return
    }

    setCaptchaFlowStatus('inProgress')

    // Check if the captcha is already solved based on the GET request
    trackInAmplitude(AmplitudeEvent.CaptchaGetStatus, {
      currentPage: currentPageInSession
    })

    try {
      const captchaStatusResponse = await getCaptchaStatus(traversalId)
      const status = captchaStatusResponse?.status

      if (status && isOk(status)) {
        trackInAmplitude(AmplitudeEvent.CaptchaStatusIsValid, {
          currentPage: currentPageInSession
        })
        setCaptchaFlowStatus('success')

        return
      }
    } catch (error) {
      trackInAmplitude(AmplitudeEvent.CaptchaGetStatusError, {
        currentPage: currentPageInSession
      })
    }

    // Run Challenge
    trackInAmplitude(AmplitudeEvent.CaptchaChallengeStarted, {
      currentPage: currentPageInSession
    })

    try {
      trackInAmplitude(AmplitudeEvent.CaptchaExecute, {
        currentPage: currentPageInSession
      })
      captchaRef.current?.execute()
    } catch (error) {
      trackInAmplitude(AmplitudeEvent.CaptchaExecuteError, {
        currentPage: currentPageInSession,
        error
      })
      setCaptchaFlowStatus('error')

      return
    }

    trackInAmplitude(AmplitudeEvent.CaptchaExecuteSuccess, {
      currentPage: currentPageInSession
    })

    return
  }

  const setToken = (token: string) => {
    setCaptchaToken(token)
  }

  const submitCaptchaToken = useCallback(
    async (token: string) => {
      trackInAmplitude(AmplitudeEvent.CaptchaPostToken, {
        currentPage: currentPageInSession
      })
      try {
        await postCaptchaToken(token, traversalId)
        trackInAmplitude(AmplitudeEvent.CaptchaPostTokenSuccess, {
          currentPage: currentPageInSession
        })
        setCaptchaFlowStatus('success')
      } catch (error) {
        trackInAmplitude(AmplitudeEvent.CaptchaPostTokenError, {
          currentPage: currentPageInSession
        })
        setCaptchaFlowStatus('error')

        return
      }

      return
    },
    [currentPageInSession, postCaptchaToken, setCaptchaFlowStatus, traversalId]
  )

  useEffect(() => {
    if (captchaToken && traversalId) {
      submitCaptchaToken(captchaToken)
      setCaptchaToken('')
    }
  }, [captchaToken, submitCaptchaToken, traversalId])

  return {
    handleCaptcha,
    submitCaptchaToken,
    shouldAwaitCaptcha,
    shouldHandleCaptcha,
    setToken
  }
}
